import type { MemoizedFn } from 'memoize-one';
import memoize from 'memoize-one';

import type {
	AnalyticsHandler,
	ExposureEvent,
	Flags,
	FlagShape,
} from '@atlaskit/feature-flag-client/types';
import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags/setBooleanFeatureFlagResolver';
import { setupEditorExperiments } from '@atlaskit/tmp-editor-statsig/setup';
import FeatureFlagClient from '@atlaskit/feature-flag-client/client';

import { fg } from '@confluence/feature-gating';

import { unwrapValue, keyById, processFeatureFlagsPure } from './processFeatureFlagsPure.ts';
import type {
	FeatureFlagsQuery_featureFlags_nodes,
	FeatureFlagsQuery_platformFeatureFlags_nodes,
} from './__types__/FeatureFlagsQuery.ts';

function normalizeValue(rawValue: FeatureFlagsQuery_featureFlags_nodes): FlagShape {
	if (typeof rawValue === 'object' && 'value' in rawValue) {
		return {
			...(rawValue.explanation ? { explanation: { ...(rawValue.explanation as any) } } : {}),
			value: unwrapValue(rawValue.value),
		};
	} else {
		return {
			value: unwrapValue(rawValue),
			explanation: undefined,
		};
	}
}

const noopAnalyticsHandler: AnalyticsHandler = {
	sendOperationalEvent: (_event: ExposureEvent) => Promise.resolve(),
};

export const processFeatureFlags: MemoizedFn<
	(
		flags: FeatureFlagsQuery_featureFlags_nodes[],
		platformFlags: FeatureFlagsQuery_platformFeatureFlags_nodes[],
	) => {
		originalFeatureFlags: {
			[objectId: string]: string | boolean;
		};
		featureFlagsWithOverrides: {
			[objectId: string]: string | boolean;
		};
		featureFlagClient: FeatureFlagClient;
	}
> = memoize(
	(
		flags: FeatureFlagsQuery_featureFlags_nodes[],
		platformFlags: FeatureFlagsQuery_platformFeatureFlags_nodes[],
	): {
		originalFeatureFlags: {
			[objectId: string]: string | boolean;
		};
		featureFlagsWithOverrides: {
			[objectId: string]: string | boolean;
		};
		featureFlagClient: FeatureFlagClient;
	} => {
		const normalizedFeatureFlags: Flags = flags ? keyById(flags, normalizeValue) : {};
		const normalizedPlatformFeatureFlags: Flags =
			platformFlags != null ? keyById(platformFlags, normalizeValue) : {};
		Object.assign(normalizedFeatureFlags, normalizedPlatformFeatureFlags);
		const featureFlagClient = new FeatureFlagClient({
			flags: normalizedFeatureFlags,
			analyticsHandler: noopAnalyticsHandler,
		});

		// This sets up tooling for the editor experiments
		setupEditorExperiments('confluence');

		// This sets up the platform feature flags
		setBooleanFeatureFlagResolver((flagKey) => {
			// eslint-disable-next-line confluence-feature-gating/static-feature-gates
			if (fg(flagKey)) {
				return true;
			}

			return false;
		});

		const allFlags = flags.concat(platformFlags);
		const original = processFeatureFlagsPure(allFlags);

		return {
			originalFeatureFlags: original,
			featureFlagsWithOverrides: original,
			featureFlagClient,
		};
	},
);
